import { PAYOUT_ADDRESS_KINDS, type PayoutAddressKind, type PayoutAddressesInput } from './types';

/** 0x followed by 40 hexadecimal characters; shared by every EVM chain. */
export const EVM_ADDRESS_REGEX = /^0x[0-9a-fA-F]{40}$/;

/** One editable string per kind, `''` for "not set". */
export type PayoutAddressDraft = Record<PayoutAddressKind, string>;

/**
 * Only EVM has a format the browser can check without a library; the other
 * chains are validated by crypto-api, so any non-empty value passes here.
 * `''` is always valid: it means "clear" (or "not configured").
 */
export function isPayoutAddressFormatValid(kind: PayoutAddressKind, value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  return kind === 'evm' ? EVM_ADDRESS_REGEX.test(trimmed) : true;
}

export function isPayoutAddressDraftValid(draft: PayoutAddressDraft): boolean {
  return PAYOUT_ADDRESS_KINDS.every((kind) => isPayoutAddressFormatValid(kind, draft[kind]));
}

/** The form's starting point: every kind present, missing ones as `''`. */
export function toPayoutAddressDraft(
  current: Partial<Record<PayoutAddressKind, string | null>> | null | undefined,
): PayoutAddressDraft {
  return {
    evm: current?.evm ?? '',
    solana: current?.solana ?? '',
    tron: current?.tron ?? '',
    btc: current?.btc ?? '',
    ltc: current?.ltc ?? '',
    doge: current?.doge ?? '',
  };
}

/**
 * The kinds whose value changed, trimmed. An unchanged field is omitted on
 * purpose: `''` means "clear" on the wire, so sending every field would wipe
 * a wallet the screen could not display (crypto-api unreachable, stale mirror).
 */
export function diffPayoutAddresses(
  current: Partial<Record<PayoutAddressKind, string | null>> | null | undefined,
  draft: PayoutAddressDraft,
): PayoutAddressesInput {
  return PAYOUT_ADDRESS_KINDS.reduce<PayoutAddressesInput>((changes, kind) => {
    const before = (current?.[kind] ?? '').trim();
    const after = draft[kind].trim();
    return before === after ? changes : { ...changes, [kind]: after };
  }, {});
}

/** The kinds a merchant has an address for, in display order. */
export function configuredPayoutAddressKinds(
  addresses: Partial<Record<PayoutAddressKind, string | null>>,
): PayoutAddressKind[] {
  return PAYOUT_ADDRESS_KINDS.filter((kind) => !!addresses[kind]?.trim());
}
